// Tiny Web Audio synth for game feedback. No audio files — every sound is
// generated from oscillators / filtered noise so nothing is bundled or
// fetched. The AudioContext is created lazily on the first sound (always
// inside a user gesture, so autoplay policy is satisfied) and resumed if the
// browser suspended it. All cues respect a persisted mute toggle.

const MUTE_KEY = 'weddingSoundMuted';

let ctx = null;
let muted = (() => {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
})();

const listeners = new Set();

export function isMuted() {
  return muted;
}

export function subscribeMuted(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setMuted(value) {
  muted = !!value;
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* storage unavailable — keep in-memory state */
  }
  listeners.forEach((fn) => fn(muted));
}

export function toggleMuted() {
  setMuted(!muted);
  return muted;
}

function getCtx() {
  if (muted) return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

let noiseBuf = null;
function getNoise(c) {
  if (!noiseBuf) {
    noiseBuf = c.createBuffer(1, c.sampleRate, c.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  return noiseBuf;
}

// Enveloped oscillator note, scheduled `at` seconds from now.
function note(freq, at, dur, { type = 'sine', gain = 0.12, glideTo, attack = 0.012 } = {}) {
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime + at;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  if (glideTo) o.frequency.exponentialRampToValueAtTime(Math.max(1, glideTo), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(c.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.03);
}

// Filtered noise burst; the band-pass center can sweep f0 -> f1.
function noise(at, dur, { f0 = 2000, f1, q = 1, gain = 0.1, attack = 0.004 } = {}) {
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime + at;
  const s = c.createBufferSource();
  s.buffer = getNoise(c);
  const f = c.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.setValueAtTime(f0, t0);
  if (f1) f.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t0 + dur);
  f.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  s.connect(f).connect(g).connect(c.destination);
  s.start(t0);
  s.stop(t0 + dur + 0.02);
}

// Bell-ish chime: fundamental + a couple of decaying partials.
function bell(freq, at, dur, gain = 0.1) {
  [
    [1, 1],
    [2.0, 0.45],
    [3.0, 0.2],
  ].forEach(([m, gm]) =>
    note(freq * m, at, dur * (1 - 0.1 * m), { type: 'sine', gain: gain * gm, attack: 0.004 }),
  );
}

function seq(steps, opts) {
  steps.forEach(([f, a, d]) => note(f, a, d, opts));
}

// An opening swell that rides the sun/moon rising in on the game screen (the
// orb animates y:900→0 over ~4.2s): a warm pad climbing an octave, with a
// gentle chord + bell blooming as it clears the horizon.
export function playOpening() {
  note(196.0, 0, 3.6, { type: 'sine', gain: 0.06, glideTo: 392, attack: 0.8 });
  note(261.63, 0.2, 3.4, { type: 'sine', gain: 0.04, glideTo: 523.25, attack: 1.0 });
  seq(
    [
      [659.25, 2.0, 1.6],
      [783.99, 2.3, 1.6],
      [1046.5, 2.6, 1.8],
    ],
    { type: 'sine', gain: 0.035, attack: 0.3 },
  );
  bell(523.25, 2.4, 1.6, 0.04);
}

// Memory-match card flip — a light airy riffle ("ffup"): a quick sweep up
// then a short sweep down. No low body thud.
export function playFlip() {
  noise(0, 0.05, { f0: 1100, f1: 2700, q: 0.9, gain: 0.055 });
  noise(0.05, 0.055, { f0: 2400, f1: 1300, q: 0.9, gain: 0.038 });
}

// A puzzle is solved — fires the instant the win/celebration animation
// starts (not when the modal later closes). Short celebratory flourish.
export function playPuzzleWin() {
  seq(
    [
      [523, 0, 0.16],
      [659, 0.12, 0.16],
      [784, 0.24, 0.2],
      [1047, 0.4, 0.42],
    ],
    { type: 'triangle', gain: 0.06 },
  );
  bell(1047, 0.42, 0.9, 0.05);
  [0, 1, 2].forEach((i) =>
    note(1400 + i * 200, 0.5 + i * 0.08, 0.35, { type: 'sine', gain: 0.02 - i * 0.004 }),
  );
}

// A very subtle "travel" cue as an avatar glides to the next checkpoint —
// just an airy whoosh that settles, plus a faint landing tick. `lead` is how
// long the avatar waits before it actually moves: solo avatars spring after a
// ~1.2s delay; the merged/joint avatar moves almost immediately.
export function playTravel(lead = 1.2) {
  noise(lead, 1.2, { f0: 740, f1: 360, q: 0.5, gain: 0.015, attack: 0.12 });
}

// Two memory cards matched — gentle ascending two-note.
export function playMatch() {
  seq(
    [
      [660, 0, 0.1],
      [880, 0.08, 0.16],
    ],
    { type: 'sine', gain: 0.05 },
  );
}

// A puzzle piece settling into place (sliding-tile slide / jigsaw snap) —
// a short dry slide plus a woody "tok".
export function playMove() {
  noise(0, 0.06, { f0: 600, f1: 380, q: 0.6, gain: 0.03 });
  note(250, 0.05, 0.09, { type: 'triangle', gain: 0.075, glideTo: 170 });
  noise(0.055, 0.014, { f0: 3200, q: 1.2, gain: 0.02 });
}

// A correct answer / cleared puzzle step — celebratory rising arpeggio.
export function playCorrect() {
  seq(
    [
      [523.25, 0, 0.16],
      [659.25, 0.13, 0.16],
      [783.99, 0.26, 0.34],
    ],
    { type: 'sine', gain: 0.06 },
  );
}

// A wrong answer — gentle, non-harsh descending blip.
export function playWrong() {
  seq(
    [
      [300, 0, 0.12],
      [250, 0.09, 0.18],
    ],
    { type: 'sine', gain: 0.05 },
  );
}

// The two avatars merging — beat-synced to the 6s dance (11 keyframes, a
// beat every 0.6s). Smooth, romantic tones throughout: long attacks, sine
// waves, warm pad underneath, gentle bell accents instead of sharp hits.
export function playMerge() {
  // Warm pad rising slowly underneath the whole dance.
  note(196, 0, 6.0, { type: 'sine', gain: 0.035, glideTo: 392, attack: 1.2 });
  note(294, 0.1, 5.8, { type: 'sine', gain: 0.02, glideTo: 523, attack: 1.5 });
  // On-beat melody — soft sine with long attacks for a legato feel.
  const beat = [392, 440, 523, 587, 659, 784, 880, 784, 988, 1047];
  beat.forEach((f, i) =>
    note(f, i * 0.6, 0.5, { type: 'sine', gain: i >= 5 ? 0.04 : 0.03, attack: 0.08 }),
  );
  // 3.0s twirl — gentle bell instead of noise burst.
  bell(659, 3.0, 0.7, 0.03);
  bell(784, 3.15, 0.6, 0.025);
  bell(988, 3.3, 0.8, 0.02);
  // 3.6s 360° spin — soft airy sweep, very quiet.
  noise(3.6, 0.5, { f0: 1200, f1: 600, q: 0.4, gain: 0.012, attack: 0.1 });
  bell(1175, 3.6, 0.6, 0.025);
  bell(1318, 3.75, 0.7, 0.02);
  // 4.8–5.7 accelerando — smooth sine with gentle swells.
  [
    [988, 4.8],
    [1047, 5.1],
    [1175, 5.4],
    [1318, 5.7],
  ].forEach(([f, a]) => note(f, a, 0.4, { type: 'sine', gain: 0.035, attack: 0.06 }));
  // 6.0–7.0s the FUSE — a warm rising tone, no harsh noise.
  note(196, 6.0, 1.0, { type: 'sine', gain: 0.035, glideTo: 440, attack: 0.3 });
  note(294, 6.0, 1.0, { type: 'sine', gain: 0.025, glideTo: 587, attack: 0.4 });
  noise(6.0, 1.0, { f0: 300, f1: 800, q: 0.3, gain: 0.012, attack: 0.4 });
  // ~7.0s the moment they become one — a soft bloom, not a bloop.
  bell(523, 7.0, 1.2, 0.06);
  bell(659, 7.05, 1.0, 0.04);
  note(262, 7.0, 0.8, { type: 'sine', gain: 0.05, attack: 0.06 });
  // 7.2s+ calm landing — warm major chord swelling in gently.
  seq(
    [
      [392, 7.3, 2.0],
      [494, 7.36, 2.0],
      [587, 7.42, 2.2],
    ],
    { type: 'sine', gain: 0.032, attack: 0.5 },
  );
  note(196, 7.4, 2.0, { type: 'sine', gain: 0.028, attack: 0.6 });
  // Soft high shimmer — slow fade-in, not sparkly plinks.
  [0, 1, 2].forEach((i) =>
    note(1047 + i * 150, 7.2 + i * 0.15, 1.0, { type: 'sine', gain: 0.018 - i * 0.004, attack: 0.2 }),
  );
}

// Reaching the altar — just the famous opening phrase of Wagner's Bridal
// Chorus ("daaaaa dun da duuuuunnn"), slow and warm: each note layered with
// its octave below for an organ-like body (sine, not bright/digital), the
// final note held long over a soft major chord that swells then cools off.
export function playFinale() {
  const mel = [
    [523.25, 0.0, 0.9], // "daaaaa"  C5
    [698.46, 0.95, 0.45], // "dun"    F5
    [698.46, 1.45, 0.4], // "da"      F5
    [698.46, 1.9, 3.8], // "duuuuunnn" F5, long — cools off slowly
  ];
  mel.forEach(([f, a, d]) => {
    note(f, a, d, { type: 'sine', gain: 0.07, attack: 0.05 });
    note(f / 2, a, d, { type: 'sine', gain: 0.035, attack: 0.06 });
  });
  // Gentle octave shimmer + a slow, warm F-major resolve under the held note.
  note(1046.5, 1.9, 3.6, { type: 'sine', gain: 0.022, attack: 0.1 });
  seq(
    [
      [174.61, 1.9, 4.0],
      [220.0, 1.95, 4.0],
      [261.63, 2.0, 4.2],
    ],
    { type: 'sine', gain: 0.028, attack: 0.7 },
  );
}
